// CreditNova API Client
import { toast } from '@/hooks/use-toast';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface ApiError {
  statusCode: number;
  error: string;
  message: string;
}

export class ApiClientError extends Error {
  constructor(
    public statusCode: number,
    public error: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// Global error handler for API errors
function handleApiError(error: ApiClientError, endpoint: string): void {
  console.error(`API Error [${endpoint}]:`, error);

  // Determine user-friendly message based on error type
  let title = 'Error';
  let description = error.message;

  if (error.statusCode === 409) {
    // 409 Conflict — typically idempotency guard (e.g., report already in progress).
    // Show as success/info, not as an error.
    toast({
      title: 'Already In Progress',
      description: error.message || 'This operation is already in progress.',
      variant: 'success',
    });
    return; // Skip the destructive toast below
  } else if (error.statusCode === 401) {
    title = 'Authentication Error';
    description = 'Your session may have expired or the service credentials are invalid. Please try again or contact support.';
  } else if (error.statusCode === 403) {
    title = 'Access Denied';
    description = 'You do not have permission to perform this action.';
  } else if (error.statusCode === 404) {
    title = 'Not Found';
    description = 'The requested resource could not be found.';
  } else if (error.statusCode === 408 || error.message.toLowerCase().includes('timeout')) {
    title = 'Request Timeout';
    description = 'The request took too long to complete. Please try again.';
  } else if (error.statusCode >= 500) {
    title = 'Server Error';
    description = 'An unexpected error occurred on the server. Please try again later.';
  } else if (error.statusCode === 0 || error.message.toLowerCase().includes('network') || error.message.toLowerCase().includes('fetch')) {
    title = 'Connection Error';
    description = 'Unable to connect to the server. Please check your internet connection.';
  }

  // Show toast notification
  toast({
    title,
    description,
    variant: 'destructive',
  });
}

// Handle network/fetch errors
function handleNetworkError(error: unknown, endpoint: string): ApiClientError {
  console.error(`Network Error [${endpoint}]:`, error);

  const apiError = new ApiClientError(
    0,
    'NetworkError',
    error instanceof Error ? error.message : 'Failed to connect to server'
  );

  toast({
    title: 'Connection Error',
    description: 'Unable to connect to the server. Please check your internet connection and try again.',
    variant: 'destructive',
  });

  return apiError;
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
  workflowId?: string;
}

let getAccessToken: (() => Promise<string>) | null = null;
let activeWorkflowId: string | null = null;

// Promise that resolves when token provider is set
let tokenProviderReadyResolve: () => void;
const tokenProviderReady = new Promise<void>((resolve) => {
  tokenProviderReadyResolve = resolve;
});

export function setTokenProvider(provider: () => Promise<string>) {
  console.log('Token provider set');
  getAccessToken = provider;
  tokenProviderReadyResolve(); // Signal that provider is ready
}

export function setWorkflowId(id: string | null) {
  activeWorkflowId = id;
}

// Helper to wait for token provider and get token.
// Exported so that non-fetch callers (e.g., EventSource SSE) can obtain a token.
export async function waitForTokenAndGet(): Promise<string | null> {
  await tokenProviderReady;
  
  if (!getAccessToken) {
    console.warn('Token provider not available after ready signal');
    return null;
  }
  
  try {
    return await getAccessToken();
  } catch (error) {
    console.warn('Failed to get access token:', error);
    return null;
  }
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { params, workflowId, ...fetchOptions } = options;
  const method = (fetchOptions.method || 'GET').toUpperCase();  /// Provided GET as a fallback

  const effectiveWorkflowId = workflowId || activeWorkflowId;

  // Build URL with query params
  let url = `${API_URL}${endpoint}`;
  const searchParams = new URLSearchParams(params);

  // Attach workflowId to query params for GET requests (and others as a fallback)
  // This is so that the billing system can keep track of the workflowId
  // Skip if workflowId is already explicitly provided in params to avoid duplication
  if (effectiveWorkflowId && !searchParams.has('workflowId')) {
    searchParams.append('workflowId', effectiveWorkflowId);
  }

  if (searchParams.toString()) {
    url += `?${searchParams.toString()}`;
  }

  // Wait for token provider to be ready and get token
  const token = await waitForTokenAndGet();
  console.log('Token obtained for request:', endpoint, token ? 'yes' : 'no');

  // Build headers
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // ONLY add application/json if we aren't sending FormData AND there is a body
  if (!(options.body instanceof FormData) && options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    console.warn('No token available, request will be unauthenticated');
  }

  // Attach empty body for write methods if body is not there
  const methodsRequiringBody = ['POST', 'PUT', 'PATCH'];
  if (methodsRequiringBody.includes(method)) {
    if (!(fetchOptions.body instanceof FormData) && typeof fetchOptions.body === 'string') {
      try {
        const bodyObj = JSON.parse(fetchOptions.body || '{}');
        if (effectiveWorkflowId && !bodyObj.workflowId) {
          bodyObj.workflowId = effectiveWorkflowId;
          fetchOptions.body = JSON.stringify(bodyObj); // Only stringify if we changed something
        }
      } catch (e) {
        console.warn('API Client: Could not inject workflowId into non-JSON body');
      }
    } 
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...fetchOptions,
      headers,
    });
  } catch (error) {
    // Network error (no connection, DNS failure, etc.)
    throw handleNetworkError(error, endpoint);
  }

  // Handle non-JSON responses
  const contentType = response.headers.get('content-type');
  if (contentType && !contentType.includes('application/json')) {
    if (!response.ok) {
      const error = new ApiClientError(response.status, 'Error', 'Request failed');
      handleApiError(error, endpoint);
      throw error;
    }
    return response as unknown as T;
  }

  const data = await response.json();

  if (!response.ok) {
    const error = new ApiClientError(
      response.status,
      data.error || 'Error',
      data.message || 'Request failed'
    );
    handleApiError(error, endpoint);
    throw error;
  }

  return data;
}

// API Client methods
export const api = {
  // Auth
  auth: {
    session: () => request<{ user: any; authenticated: boolean }>('/api/auth/session'),
    logout: () => request('/api/auth/logout', { method: 'POST', body: JSON.stringify({}) }),
  },

  // Users
  users: {
    me: () => request<any>('/api/users/me'),
    update: (data: any) => request('/api/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    list: () => request<{ data: any[]; total: number }>('/api/users'),
    get: (id: string) => request<any>(`/api/users/${id}`),
    updateUser: (id: string, data: any) => request(`/api/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  },

  // Documents
  documents: {
    upload: async (file: File, documentType: string) => {
      const formData = new FormData();
      formData.append('file', file);

      // Wait for token provider to be ready and get token
      const token = await waitForTokenAndGet();
      const effectiveWorkflowId = activeWorkflowId;
      
      if (!token) {
        const error = new ApiClientError(401, 'Unauthorized', 'Failed to get authentication token');
        handleApiError(error, '/api/documents/upload');
        throw error;
      }

      // Add AbortController for fetch timeout
      const controller = new AbortController();
      const fetchTimeout = setTimeout(() => controller.abort(), 60000); // 60 second timeout for upload

      try {
        // Append workflowId to the upload query string
        const url = `${API_URL}/api/documents/upload?documentType=${documentType}${
          effectiveWorkflowId ? `&workflowId=${effectiveWorkflowId}` : ''
        }`;
        
        const response = await fetch(url, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
          signal: controller.signal,
        });

        clearTimeout(fetchTimeout);

        const data = await response.json();
        if (!response.ok) {
          const error = new ApiClientError(response.status, data.error, data.message);
          handleApiError(error, '/api/documents/upload');
          throw error;
        }
        return data;
      } catch (error: any) {
        clearTimeout(fetchTimeout);
        if (error.name === 'AbortError') {
          const timeoutError = new ApiClientError(408, 'Timeout', 'Upload request timed out');
          handleApiError(timeoutError, '/api/documents/upload');
          throw timeoutError;
        }
        // If it's already an ApiClientError, it's been handled
        if (error instanceof ApiClientError) {
          throw error;
        }
        // Network error
        throw handleNetworkError(error, '/api/documents/upload');
      }
    },
    list: () => request<{ data: any[]; total: number }>('/api/documents'),
    get: (id: string) => request<any>(`/api/documents/${id}`),
    delete: (id: string) => request(`/api/documents/${id}`, { method: 'DELETE' }),
    download: (id: string) => `${API_URL}/api/documents/${id}/download`,
    integrityReports: (documentIds: string[]) =>
      request<{ data: Record<string, any> }>('/api/documents/integrity-reports', {
        method: 'POST',
        body: JSON.stringify({ documentIds }),
      }),

    // ---------------------------------------------------------------
    // SSE Ticket — exchanges the Bearer JWT for a short-lived,
    // single-use ticket to pass as a query param when opening an
    // EventSource (which cannot send custom headers).
    // ---------------------------------------------------------------
    sseTicket: () =>
      request<{ ticket: string }>('/api/documents/sse-ticket', {
        method: 'POST',
        body: JSON.stringify({}),
      }),

    // ---------------------------------------------------------------
    // Batch Upload — Concurrent Workflow
    //
    // Uploads multiple files at once, each tagged with a documentType,
    // period label, and display label. The backend creates individual
    // document records and enqueues BullMQ jobs for each file.
    //
    // Parameters:
    //   files     — Array of File objects to upload
    //   metadata  — JSON payload describing each file:
    //               { workflowId, items: [{ documentType, period, label }] }
    //               items[i] corresponds to files[i] by index.
    //
    // Returns: { documents: Array<{ id, documentType, status, ... }> }
    // ---------------------------------------------------------------
    uploadBatch: async (
      files: File[],
      metadata: {
        workflowId: string;
        items: Array<{ documentType: string; period: string; label: string }>;
      },
    ) => {
      const token = await waitForTokenAndGet();
      if (!token) {
        const error = new ApiClientError(401, 'Unauthorized', 'Failed to get authentication token');
        handleApiError(error, '/api/documents/upload-batch');
        throw error;
      }

      const formData = new FormData();
      // Append each file under the same field name — multer / fastify-multipart
      // collects them as an array.
      files.forEach((file) => formData.append('files', file));
      // Metadata is sent as a JSON string in a separate form field.
      formData.append('metadata', JSON.stringify(metadata));

      const controller = new AbortController();
      // Allow 3 minutes for large batch uploads (up to 20 files × 50 MB each)
      const fetchTimeout = setTimeout(() => controller.abort(), 180_000);

      try {
        const effectiveWorkflowId = activeWorkflowId;
        const url = `${API_URL}/api/documents/upload-batch${
          effectiveWorkflowId ? `?workflowId=${effectiveWorkflowId}` : ''
        }`;

        const response = await fetch(url, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
          signal: controller.signal,
        });

        clearTimeout(fetchTimeout);
        const data = await response.json();

        if (!response.ok) {
          const error = new ApiClientError(response.status, data.error, data.message);
          handleApiError(error, '/api/documents/upload-batch');
          throw error;
        }

        return data;
      } catch (error: any) {
        clearTimeout(fetchTimeout);
        if (error.name === 'AbortError') {
          const timeoutError = new ApiClientError(408, 'Timeout', 'Batch upload timed out');
          handleApiError(timeoutError, '/api/documents/upload-batch');
          throw timeoutError;
        }
        if (error instanceof ApiClientError) throw error;
        throw handleNetworkError(error, '/api/documents/upload-batch');
      }
    },

    // Fetch current document statuses for a workflow (for resume functionality)
    statuses: (workflowId: string) =>
      request<{ data: Array<{ id: string; name: string; documentType: string; status: string }>; total: number }>(`/api/documents/statuses/${workflowId}`),

  },

  // Analyses
  analyses: {
    create: (data: any) => request('/api/analyses', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    list: () => request<{ data: any[]; total: number }>('/api/analyses'),
    get: (id: string) => request<any>(`/api/analyses/${id}`),
    getByReportId: (reportId: string) => request<any>(`/api/analyses/report/${reportId}`),
    update: (id: string, data: any) => request(`/api/analyses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    delete: (id: string) => request(`/api/analyses/${id}`, { method: 'DELETE' }),
    // Export analysis as PDF - returns ArrayBuffer for download
    exportPdf: async (id: string): Promise<ArrayBuffer> => {
      const token = await waitForTokenAndGet();

      let response: Response;
      try {
        response = await fetch(`${API_URL}/api/analyses/${id}/export`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
      } catch (error) {
        throw handleNetworkError(error, `/api/analyses/${id}/export`);
      }

      if (!response.ok) {
        const errorData = await response.json();
        const error = new ApiClientError(response.status, errorData.error, errorData.message);
        handleApiError(error, `/api/analyses/${id}/export`);
        throw error;
      }

      return response.arrayBuffer();
    },
  },

  // Templates
  templates: {
    create: (data: any) => request('/api/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    list: () => request<{ data: any[]; total: number }>('/api/templates'),
    get: (id: string) => request<any>(`/api/templates/${id}`),
    update: (id: string, data: any) => request(`/api/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    delete: (id: string) => request(`/api/templates/${id}`, { method: 'DELETE' }),
    duplicate: (id: string) => request(`/api/templates/${id}/duplicate`, { method: 'POST', body: JSON.stringify({}) }),
  },

  // Jobs
  jobs: {
    get: (id: string) => request<any>(`/api/jobs/${id}`),
    list: () => request<{ data: any[]; total: number }>('/api/jobs'),
    cancel: (id: string) => request(`/api/jobs/${id}/cancel`, { method: 'POST', body: JSON.stringify({}) }),
    retry: (id: string) => request(`/api/jobs/${id}/retry`, { method: 'POST', body: JSON.stringify({}) }),
  },

  // Extraction
  extraction: {
    preprocess: (data: any) => request<any>('/api/extraction/preprocess', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    // Extract data from uploaded document (financial, credit, bank, or corporate)
    extract: (data: {
      uploadId: string;
      workflowId?: string;
      period?: string;
      detailLevel?: string;
      bankStatementType?: 'individual' | 'business' | 'dynamic_capital';
      selectedCategories?: string[];
    }) =>
      request<any>('/api/extraction/extract', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    // Unmask PII-redacted data server-side using stored token maps.
    // extractionIds: the extraction UUIDs whose token maps to merge.
    // data: the masked data structure to restore.
    unmask: (data: { extractionIds: string[]; data: unknown }) =>
      request<{ data: unknown }>('/api/extraction/unmask', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  // Cross-period analysis
  analysis: {
    crossPeriod: (data: {
      workflowId: string;
      documentType: string;
      extractions: any[];
      periods: string[];
    }) => request<any>('/api/multiFiles/cross-period', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  },

  // Reports
  reports: {
    generate: (data: any) => request<any>('/api/reports/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    summary: (data: any) => request<any>('/api/reports/summary', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    financialStatement: (data: any) => request<any>('/api/reports/financial-statement', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    bankStatement: (data: any) => request<any>('/api/reports/bank-statement', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    // Poll for report completion status (fallback for missed SSE events)
    status: (workflowId: string) => request<{
      success: boolean;
      status: 'complete' | 'completed' | 'processing';
      analysisId?: string;
      reportId?: string;
    }>('/api/reports/status', { params: { workflowId } }),
    // Run a single Phase 1 agent early and cache the result server-side.
    // Fire-and-forget — if it fails, the full pipeline retries during report gen.
    runPhase1: (data: {
      workflowId: string;
      agentType: 'banking-analyst' | 'credit-profile' | 'corporate-legal';
      writeupType: string;
      summaryPreferences?: any;
      bankStatementFindings?: any;
      allBankStatementExtractions?: any[];
      creditReportData?: any;
      selectedCreditReportItems?: any;
      allCreditReportExtractions?: any[];
      accountData?: any;
      allCorporateExtractions?: any[];
    }) => request<{ success: boolean; agentType: string; cached: boolean }>('/api/reports/run-phase1', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  },

  // PDF Generation
  pdf: {
    creditReport: async (data: any) => {
      // Wait for token provider to be ready and get token
      const token = await waitForTokenAndGet();

      let response: Response;
      try {
        response = await fetch(`${API_URL}/api/pdf/credit-report`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(data),
        });
      } catch (error) {
        throw handleNetworkError(error, '/api/pdf/credit-report');
      }

      if (!response.ok) {
        const errorData = await response.json();
        const error = new ApiClientError(response.status, errorData.error, errorData.message);
        handleApiError(error, '/api/pdf/credit-report');
        throw error;
      }

      return response.arrayBuffer();
    },
    bankStatement: async (data: any) => {
      // Wait for token provider to be ready and get token
      const token = await waitForTokenAndGet();

      let response: Response;
      try {
        response = await fetch(`${API_URL}/api/pdf/bank-statement`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(data),
        });
      } catch (error) {
        throw handleNetworkError(error, '/api/pdf/bank-statement');
      }

      if (!response.ok) {
        const errorData = await response.json();
        const error = new ApiClientError(response.status, errorData.error, errorData.message);
        handleApiError(error, '/api/pdf/bank-statement');
        throw error;
      }

      return response.arrayBuffer();
    },
    financialStatement: async (data: any) => {
      // Wait for token provider to be ready and get token
      const token = await waitForTokenAndGet();

      let response: Response;
      try {
        response = await fetch(`${API_URL}/api/pdf/financial-statement`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(data),
        });
      } catch (error) {
        throw handleNetworkError(error, '/api/pdf/financial-statement');
      }

      if (!response.ok) {
        const errorData = await response.json();
        const error = new ApiClientError(response.status, errorData.error, errorData.message);
        handleApiError(error, '/api/pdf/financial-statement');
        throw error;
      }

      return response.arrayBuffer();
    },
  },
  
  // API-Keys
  apiKeyManagement: {
    listKeys: () => request<{ data: any[]; total: number }>('/api/apiKeyManagement'),

    getKey: (keyId: string) => request<any>(`/api/apiKeyManagement/${keyId}`),
      
    getAvailableRoutes: () => request<{ success: boolean; data: any[] }>('/api/apiKeyManagement/availableRoutes'),

    createKey: (name: string) => request<any>('/api/apiKeyManagement/create', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

    revokeKey: (keyId: string) => request(`/api/apiKeyManagement/${keyId}`, {
      method: 'DELETE',
    }),

    updateKey: (keyId: string, data: any) => request(`/api/apiKeyManagement/${keyId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

    rotateKey: (keyId: string, options?: { keepOldKey: boolean }) => 
    request<any>(`/api/apiKeyManagement/${keyId}/rotate`, {
      method: 'POST',
      body: JSON.stringify(options || { keepOldKey: false }),
    }),

    getUsage: (keyId: string, days: number = 30) => 
      request<{ success: boolean; data: any }>(`/api/apiKeyManagement/${keyId}/usage?days=${days}`),

    // Admin only: Get all routes and their public/private status for gateway access control
    getRoutePermissions: () => 
      request<any[]>('/api/apiKeyManagement/routePermissions'),

    updateRoutePermission: (data: {
      path: string;
      method: string;
      isPublic: boolean;
      category: string;
    }) => request<any>('/api/apiKeyManagement/routePermissions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    deleteRoutePermission: (path: string, method: string) => request(`/api/apiKeyManagement/routePermissions`, {
      method: 'DELETE',
      body: JSON.stringify({ path, method }),
    }),
  },

  billing: {
    dashBoard: () => request<any[]>('/api/billing/dashboard'),
    getClientDetail: (clientId: string) => request<any>(`/api/billing/${clientId}`),
    myUsage: () => request<any>('/api/billing/my-usage'),
  },

  // Webhook Deliveries
  webhooks: {
    listDeliveries: (params?: { limit?: number }) =>
      request<{ data: any[]; total: number }>('/api/webhooks/deliveries', {
        params: params as Record<string, string>,
      }),
  },

  clients: {
    listClients: () => request<any[]>('/api/clients/'),
    listClientUsers: (clientId: string) =>  request<any[]>(`/api/clients/${clientId}/users`),
    get: (clientId: string) => request<any>(`/api/clients/${clientId}`),
    create: (data: { name: string; email: string; slug: string; planTier?: string; billingAnchorDay?: number }) =>
      request<{ success: boolean; data: any }>('/api/clients/', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  // Extraction Overrides
  overrides: {
    list: (query: { workflowId?: string; analysisId?: string }, source?: string) =>
      request<{ success: boolean; data: any[] }>('/api/overrides', {
        params: { ...query, ...(source ? { source } : {}) },
      }),
    create: (data: {
      workflowId: string;
      extractionId?: string;
      analysisId?: string;
      source: string;
      period?: string;
      fieldPath: string;
      fieldLabel: string;
      originalValue: string;
      overrideValue: string;
      justification: string;
    }) =>
      request<{ success: boolean; data: any }>('/api/overrides', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: { overrideValue: string; justification: string }) =>
      request<{ success: boolean; data: any }>(`/api/overrides/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ success: boolean }>(`/api/overrides/${id}`, { method: 'DELETE' }),
  },

  // Workflows
  workflows: {
    create: (data: { summaryPreferences?: any }) =>
      request<{ id: string; analysisId: string; workflowState: string; createdAt: string }>('/api/workflows', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    resume: (workflowId: string) =>
      request<any>(`/api/workflows/${workflowId}`),
    update: (workflowId: string, data: any) =>
      request<any>(`/api/workflows/${workflowId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    createAnalysis: (workflowId: string, analysisId: string, businessName?: string) =>
      request<{ id: string; status: string; reportId: string }>(`/api/workflows/${workflowId}/analysis`, {
        method: 'POST',
        body: JSON.stringify({ analysisId, ...(businessName ? { businessName } : {}) }),
      }),
    status: (workflowId: string) =>
      request<{ id: string; workflowState: string; updatedAt: string }>(`/api/workflows/${workflowId}/status`),
    delete: (workflowId: string) =>
      request<{ success: boolean }>(`/api/workflows/${workflowId}`, { method: 'DELETE' }),
  },

  // Onboarding (invite links + join requests)
  onboarding: {
    // Status check (works without clientId)
    status: () => request<any>('/api/onboarding/status'),

    // Invite validation & redemption (works without clientId)
    validateInvite: (token: string) => request<any>(`/api/onboarding/invites/validate/${token}`),
    redeemInvite: (token: string) => request<any>(`/api/onboarding/invites/redeem/${token}`, { method: 'POST', body: JSON.stringify({}) }),

    // Join requests (user-initiated, works without clientId)
    submitJoinRequest: (organizationSlug: string) =>
      request<any>('/api/onboarding/join-requests', {
        method: 'POST',
        body: JSON.stringify({ organizationSlug }),
      }),
    myJoinRequests: () => request<any[]>('/api/onboarding/join-requests/mine'),

    // Admin: invite management
    createInvite: (data: { clientId: string; role?: string; email?: string; expiresInDays?: number }) =>
      request<any>('/api/onboarding/invites', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    listInvites: (params?: { clientId?: string; status?: string }) =>
      request<any[]>('/api/onboarding/invites', { params: params as Record<string, string> }),
    revokeInvite: (id: string) =>
      request<any>(`/api/onboarding/invites/${id}`, { method: 'DELETE' }),

    // Admin: join request management
    listJoinRequests: (params?: { status?: string }) =>
      request<any[]>('/api/onboarding/join-requests', { params: params as Record<string, string> }),
    reviewJoinRequest: (id: string, data: { action: 'approve' | 'deny'; clientId?: string; role?: string; denyReason?: string }) =>
      request<any>(`/api/onboarding/join-requests/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
  },

};

export default api;